/**
 * Component: Quantity selector
 * ------------------------------------------------------------------------------
 * A form control component to increment/decrement a counter.
 *
 * @namespace quantitySelector
 */
import {on} from '../helpers/utils';
import bind from '../mixins/bind';
import {misc as icons} from '../helpers/svg-map';

/**
 * DOM selectors.
 */
const selectors = {
  decrement: '[js-quantity-selector="decrement"]',
  increment: '[js-quantity-selector="increment"]',
  input: '[js-quantity-selector="input"]',
};

/**
 * Create a new quantity selector object.
 * @param {String} selector - Node id selector.
 * @param {Function} callback - Callback function executed after a quantity update.
 */
export function quantitySelector(selector, callback = null) {
  // Instance settings.
  const namespace = selector;
  const container = document.getElementById(selector);

  // Node selectors.
  const nodeSelectors = {
    decrement: container.querySelector(selectors.decrement),
    increment: container.querySelector(selectors.increment),
    input: container.querySelector(selectors.input),
  };

  /**
   * Initialise component bind.
   */
  const binder = bind(container);

  /**
   * Initialise component.
   */
  function init() {
    if (binder.isSet()) {
      return;
    }

    setEventBusListeners();
    setEventListeners();
    binder.set();

    if (container.dataset.available === 'false') {
      nodeSelectors.decrement.setAttribute('disabled', true);
      nodeSelectors.increment.setAttribute('disabled', true);
    }

    if (nodeSelectors.input.value <= 1) {
      nodeSelectors.decrement.setAttribute('disabled', true);
    }

    if (nodeSelectors.input.value === container.getAttribute('data-max')) {
      nodeSelectors.increment.setAttribute('disabled', true);
    }
  }

  /**
   * Set listeners for each instance.
   */
  function setEventBusListeners() {
    Karmoon.EventBus.listen(`${namespace}:update`, (value) => {
      update(value);
      executeCallback();
    });
  }

  /**
   * Set click listeners.
   */
  function setEventListeners() {
    on('click', nodeSelectors.increment, (event) =>
      handleQuantityUpdate('increment', event),
    );
    on('click', nodeSelectors.decrement, (event) =>
      handleQuantityUpdate('decrement', event),
    );
  }

  /**
   * Handle quantity updates.
   * @param {String} type - Quantity update type (increment/decrement).
   * @param {Event} event - Click event.
   */
  function handleQuantityUpdate(type, event) {
    event.preventDefault();
    const inputValue = nodeSelectors.input.value;
    const currentValue =
      type === 'increment'
        ? parseInt(inputValue, 10) + 1
        : parseInt(inputValue, 10) - 1;
    handleButtonState(currentValue);

    Karmoon.EventBus.emit(`${namespace}:update`, currentValue);
  }

  /**

  /**
   * Update DOM value and execute callback.
   * @param {Integer} value - Value to replace on DOM.
   */
  function update(value) {
    nodeSelectors.input.setAttribute('value', value);
  }

  /**
   * Handle maximum threshold state.
   * @param {Integer} currentValue - Predicted value to validate.
   */
  function handleButtonState(currentValue) {
    // Disables increment button if the current value to more than or equal to the inventory quantity.
    if (currentValue >= getMaxThresholdValue()) {
      nodeSelectors.increment.setAttribute('disabled', true);

      // If the increment is disabled but the current value is over 1 the decrement button is enabled.
      if (currentValue > 1) {
        nodeSelectors.decrement.removeAttribute('disabled');
        return;
      }
      return;
    }

    nodeSelectors.increment.removeAttribute('disabled');

    // If the current value is less than or equal to 1 the decrement button is disabled
    if (currentValue <= 1) {
      nodeSelectors.decrement.setAttribute('disabled', true);
      return;
    }

    nodeSelectors.decrement.removeAttribute('disabled');
  }

  /**
   * Get maximum threshold value of inventory.
   * @returns {Number}
   */
  function getMaxThresholdValue() {
    return Number(container.getAttribute('data-max'));
  }

  // Execute callback function defined in constructor.
  function executeCallback() {
    return typeof callback === 'function' ? callback() : false;
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
    update,
  });
}

/**
 * Get quantity selector template.
 * @param {Object} item - Cart item to get properties from.
 * @param {Integer} index - Unique index to bind component to.
 * @returns {String}
 */
export function quantitySelectorTemplate(item, index) {
  let dataMax = item.properties;

  if (dataMax === null) {
    dataMax = '';
  } else {
    dataMax = dataMax._max_inventory;
  }

  return `
  <div data-label="Quantity">
    <label for="Quantity-${index}" class="label visually-hidden">Quantity</label>

    <div
      id="QuantitySelector-${index}"
      class="quantity-selector quantity-selector--small"
      data-max="${dataMax}"
      js-cart="quantitySelector"
    >
      <div class="quantity-selector__control">
        <button
          class="quantity-selector__button"
          value="decrement"
          js-quantity-selector="decrement"
        >
          <span class="visually-hidden">-</span>
          ${icons.minus}
        </button>
      </div>

      <div class="quantity-selector__control">
        <input
          id="Quantity-${index}"
          class="quantity-selector__input"
          min="1"
          name="quantity"
          pattern="[0-9]*"
          readonly
          type="number"
          value="${item.quantity}"
          js-quantity-selector="input"
        />
      </div>

      <div class="quantity-selector__control">
        <button
          class="quantity-selector__button"
          value="increment"
          data-cy="quantityIncrease"
          js-quantity-selector="increment"
        >
          <span class="visually-hidden">+</span>
          ${icons.plus}
        </button>
      </div>
    </div>
  </div>
  `;
}
